import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_DESIGN = {
  theme: 'elegant',
  ornament: '❦ ❦ ❦',
  message: 'Nous serions honorés de votre présence pour célébrer notre union.',
  bg_color: '#FAF6EE',
  text_color: '#3D2F15',
  dress_code: '',
  rsvp_deadline: '',
  address: '',
  contact: '',
};

export function useInvitationDesign(planToken, authToken) {
  const [design,   setDesign]   = useState(DEFAULT_DESIGN);
  const [loading,  setLoading]  = useState(true);
  const [aiResults, setAiResults] = useState(null);   // []
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError,   setAiError]   = useState(null);
  const debounceRef = useRef(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  }), [authToken]);

  // Fetch initial
  const fetchDesign = useCallback(async () => {
    if (!planToken || !authToken) return;
    setLoading(true);
    try {
      const res  = await fetch(`${BACKEND}/api/plan-de-table/${planToken}/invitation-design`,
        { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) setDesign({ ...DEFAULT_DESIGN, ...data });
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [planToken, authToken, headers]);

  useEffect(() => { fetchDesign(); }, [fetchDesign]);

  // Update optimiste + debounce 500ms
  const updateDesign = useCallback((patch) => {
    setDesign(prev => ({ ...prev, ...patch }));
    if (!planToken || !authToken) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await fetch(
          `${BACKEND}/api/plan-de-table/${planToken}/invitation-design`,
          { method: 'PATCH', headers: headers(), body: JSON.stringify(patch) },
        );
      } catch { /* silent — la prochaine modif retentera */ }
    }, 500);
  }, [planToken, authToken, headers]);

  // Génération IA
  const generateAI = useCallback(async (prompt) => {
    if (!planToken || !authToken || !prompt.trim()) return;
    setAiLoading(true);
    setAiError(null);
    setAiResults(null);
    try {
      const res  = await fetch(`${BACKEND}/api/ai/invitation-examples`, {
        method:  'POST',
        headers: headers(),
        body:    JSON.stringify({ plan_token: planToken, prompt }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = res.status === 503
          ? "L'assistant IA est temporairement indisponible. Configure ANTHROPIC_API_KEY ou personnalise manuellement ci-dessous."
          : (data.detail ?? 'Erreur IA');
        setAiError(msg);
        return;
      }
      setAiResults(data.examples ?? []);
    } catch (e) {
      setAiError("Erreur réseau. Vérifie ta connexion et réessaie.");
    } finally {
      setAiLoading(false);
    }
  }, [planToken, authToken, headers]);

  // Apply example IA en bloc
  const applyAiExample = useCallback((ex) => {
    const patch = {
      theme:        ex.theme,
      bg_color:     ex.bg,
      text_color:   ex.text,
      message:      ex.message,
      ornament:     ex.ornament ?? '',
      dress_code:   ex.dress_code ?? '',
      rsvp_deadline:ex.rsvp_deadline ?? '',
    };
    updateDesign(patch);
    toast.success(`Style « ${ex.title} » appliqué`);
  }, [updateDesign]);

  return {
    design, loading, updateDesign,
    aiResults, aiLoading, aiError, generateAI, applyAiExample,
  };
}
